import { useMemo, useState } from "react";
import dayjs from "dayjs";

// Components
import Modal from "components/organism/modal-v2";
import Button from "components/v1/Button";
import ModalImages from "components/molecules/shared/modal-images";
import Loader from "components/molecules/Loader";

// Assets
import { ReactComponent as XIcon } from "assets/cross.svg";
import IDot from "assets/i-dot.js";
import IPlane from "assets/i-plane.js";
import ILock from "assets/i-lock.js";
import IMegaphone from "assets/i-megaphone.js";

import {
  useGetDetailListingOffice,
  useGetListingImages,
} from "api/list-listing/query";

dayjs.locale("id");

const ModalDetailListing = ({
  slug,
  onClose,
  isOpen,
  property_images,
  onSendListing,
  propertyId,
  ...props
}) => {
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [isOpenModalImages, setIsOpenModalImages] = useState(false);
  const { data: dataDetailListing, isLoading: isLoadingDetailListing } =
    useGetDetailListingOffice({ slug });
  const { data: dataListingImages, isLoading: isLoadingListingImages } =
    useGetListingImages({ slug });

  const Images = useMemo(() => {
    return dataListingImages?.data?.data || [];
  }, [dataListingImages]);

  const property = useMemo(() => {
    return dataDetailListing?.data?.data || {};
  }, [dataDetailListing]);

  const user = useMemo(() => {
    return dataDetailListing?.data?.data?.user || {};
  }, [dataDetailListing]);

  const locations = useMemo(() => {
    const tempLoc = dataDetailListing?.data?.data?.summary?.property_locations;
    if (!tempLoc?.length) return null;

    return {
      published: tempLoc.find((i) => i.is_publish),
      private: tempLoc.find((i) => !i.is_publish),
    };
  }, [dataDetailListing]);

  const features = useMemo(() => {
    const res = [];
    if (!!property?.bedroom) {
      res.push(
        `${property?.bedroom} ${
          property?.additional_bedroom ? `+${property?.additional_bedroom}` : ""
        } kamar tidur`,
      );
    }
    if (!!property?.bathroom) {
      res.push(
        `${property?.bathroom} ${
          property?.additional_bathroom
            ? `+${property?.additional_bathroom}`
            : ""
        } kamar mandi`,
      );
    }
    if (!!property?.number_of_floors) {
      res.push(`${property?.number_of_floors} lantai`);
    }
    if (!!property?.commission_rate) {
      res.push(`komisi ${property?.commission_rate}%`);
    }

    return res;
  }, [property]);

  const description = useMemo(() => {
    if (showFullDesc) return property?.description ?? "";
    return property?.description?.slice(0, 300);
  }, [property, showFullDesc]);

  const fasilitasProperti = useMemo(() => {
    const facilities = dataDetailListing?.data?.data?.facilities || [];
    const res = {
      left: [],
      right: [],
    };

    if (!!property?.year) {
      res.left.push(`Tahun dibangun ${property?.year}`);
    }
    if (!!property?.property_condition) {
      res.left.push(`Kondisi properti ${property?.property_condition}`);
    }
    if (!!property?.parking_capacity) {
      res.left.push(`Kapasitas parkir ${property?.parking_capacity}`);
    }
    if (!!property?.furniture_condition) {
      res.left.push(`Kondisi perabotan ${property?.furniture_condition}`);
    }
    if (!!property?.face) {
      res.left.push(`Menghadap ke ${property?.face}`);
    }
    if (!!property?.electrical_power) {
      res.right.push(`Daya listrik ${property?.electrical_power}`);
    }
    if (!!facilities?.length) {
      facilities.forEach((f) => {
        res.right.push(f.name);
      });
    }
    return res;
  }, [dataDetailListing, property]);

  const fasilitasSekitar = useMemo(() => {
    const environmentFacilities =
      dataDetailListing?.data?.data?.environment_facilities || [];
    const res = [];

    if (!!environmentFacilities?.length) {
      environmentFacilities.forEach((f) => {
        res.push(f.name);
      });
    }

    return res;
  }, [dataDetailListing]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} {...props}>
      <div
        className="w-[740px] z-[42] bg-white rounded-md border-[0.5px] border-solid border-[#ECECEC] 
        shadow-[0px_1px_3px_0px_rgba(16,16,16,0.10),_0px_1px_2px_0px_rgba(16,16,16,0.06)]"
      >
        <div className="flex justify-between px-4 py-2 border-b border-solid border-[#D8D8D9]">
          <p className="text-[17px] font-inter-semibold">Detail Listing</p>
          <XIcon onClick={onClose} />
        </div>

        {isLoadingDetailListing || isLoadingListingImages ? (
          <div className="flex items-center justify-center h-[407px]">
            <Loader />
          </div>
        ) : (
          <div className="px-4 py-2 max-h-[407px] overflow-y-auto">
            <div className="flex items-center gap-1 mb-4">
              <span className="text-[12px] tracking-[-0.4px] leading-[17px] font-inter text-[#9E9E9E]">
                Detail listing
              </span>
              <IDot />
              <span className="text-[12px] tracking-[-0.4px] leading-[21px] font-inter text-[#9E9E9E]">
                Dibuat pada{" "}
                <span className="text-[#494949]">
                  {dayjs(property?.created_at).format("DD MMMM YYYY")}
                </span>
              </span>
            </div>

            <h2 className="text-[24px] tracking-[-0.4px] leading-[32px] font-inter-bold text-[#101010]">
              {property?.title}
            </h2>
            <p className="text-[14px] tracking-[-0.4px] leading-[20px] font-inter text-[#494949] mb-4">
              {locations?.published?.area?.name},{" "}
              {locations?.published?.city?.name},{" "}
              {locations?.published?.province?.name}
            </p>

            <div className="flex items-center gap-2 mb-4">
              <span className="px-2 py-0.5 rounded-[7px] bg-[#101010] text-[12px] tracking-[-0.4px] leading-[17px] font-inter-medium text-white">
                {property?.property_privacy?.name}
              </span>
              <span className="px-2 py-0.5 rounded-[7px] bg-[#0D814A] text-[12px] tracking-[-0.4px] leading-[17px] font-inter-medium text-white">
                {property?.property_type?.name}
              </span>
              <span className="px-2 py-0.5 rounded-[7px] text-[12px] border border-solid border-[#494949] tracking-[-0.4px] leading-[17px] font-inter-medium text-[#9E9E9E]">
                {property?.property_category?.name}
              </span>
              <span className="px-2 py-0.5 rounded-[7px] text-[12px] border border-solid border-[#494949] tracking-[-0.4px] leading-[17px] font-inter-medium text-[#9E9E9E]">
                {property?.ownership_status?.name}
              </span>
              <span className="px-2 py-0.5 rounded-[7px] text-[12px] border border-solid border-[#494949] tracking-[-0.4px] leading-[17px] font-inter-medium text-[#9E9E9E]">
                #{property?.id}
              </span>
            </div>

            <div className="mb-4 grid grid-cols-2 grid-rows-[220px] items-center gap-2 rounded-[8px]">
              <div className="relative w-full h-full rounded-l-[8px]">
                <span className="absolute top-4 left-4 py-[2px] px-4 bg-[rgba(16,16,16,0.8)] rounded-[4px] text-[12px] tracking-[-0.4px] leading-[17px] font-inter text-[#fff]">
                  {Images?.[0]?.name}
                </span>
                <img
                  src={Images?.[0]?.image?.url}
                  alt="Property"
                  className="w-full h-full object-cover rounded-l-[8px]"
                />
              </div>
              <div className="flex flex-col gap-2 h-full rounded-r-[8px]">
                <div className="relative w-full h-[calc(50%-4px)] rounded-tr-[8px]">
                  <span className="absolute top-4 left-4 py-[2px] px-4 bg-[rgba(16,16,16,0.8)] rounded-[4px] text-[12px] tracking-[-0.4px] leading-[17px] font-inter text-[#fff]">
                    {Images?.[1]?.name}
                  </span>
                  <img
                    src={Images?.[1]?.image?.url}
                    alt="Property"
                    className="w-full h-full object-cover rounded-tr-[8px]"
                  />
                </div>
                <div className="relative w-full h-[calc(50%-4px)] rounded-br-[8px]">
                  <span className="absolute top-4 left-4 py-[2px] px-4 bg-[rgba(16,16,16,0.8)] rounded-[4px] text-[12px] tracking-[-0.4px] leading-[17px] font-inter text-[#fff]">
                    {Images?.[2]?.name}
                  </span>
                  <img
                    src={Images?.[2]?.image?.url}
                    alt="Property"
                    className="w-full h-full object-cover rounded-br-[8px]"
                  />
                </div>
              </div>
            </div>

            <Button
              className="mb-4"
              type="secondary"
              text="Tampilkan Semua Foto"
              onClick={() => setIsOpenModalImages(true)}
            />

            <p className="text-[30px] font-inter-semibold bg-[linear-gradient(90deg,#C1AF82_-6.5%,#75612F_50%)] bg-clip-text text-transparent">
              {parseInt(property?.price).toLocaleString("id-ID")}
            </p>

            <div className="flex items-center gap-2 mb-4 pb-4 border-b border-solid border-[#ECECEC]">
              {features?.map((item, index) => (
                <>
                  <span
                    key={`feature-${index}`}
                    className="text-[16px] font-inter-medium text-[#494949]"
                  >
                    {item}
                  </span>
                  {index < features?.length - 1 && <IDot color="#494949" />}
                </>
              ))}
            </div>

            <div className="mb-4 pb-4 border-b border-solid border-[#ECECEC]">
              <div className="flex items-start gap-2 mb-4">
                <IMegaphone />
                <div className="w-[calc(100%-24px-8px)] max-w-[514px]">
                  <p className="text-[16px] font-inter-medium text-[#101010] mb-2">
                    Alamat yang dipublikasikan
                  </p>
                  <p className="text-[14px] font-inter text-[#494949]">
                    {locations?.published?.street ?? ""}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <ILock />
                <div className="w-[calc(100%-24px-8px)] max-w-[514px]">
                  <p className="text-[16px] font-inter-medium text-[#101010] mb-2">
                    Alamat yang <span className="text-[#AF0000]">tidak</span>{" "}
                    dipublikasikan
                  </p>
                  <p className="text-[16px] font-inter text-[#494949]">
                    {locations?.private?.street ?? ""}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4 pb-4 border-b border-solid border-[#ECECEC]">
              <p className="text-[24px] font-inter-medium text-[#101010] mb-6">
                Ukuran Properti
              </p>
              <div className="flex items-center gap-2 mb-4">
                <IDot color="#494949" />
                <p className="text-[14px] font-inter text-[#494949]">
                  Luas Bangunan {property?.building_area} m<sup>2</sup>
                </p>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <IDot color="#494949" />
                <p className="text-[14px] font-inter text-[#494949]">
                  Panjang Bangunan {property?.building_length} m
                </p>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <IDot color="#494949" />
                <p className="text-[14px] font-inter text-[#494949]">
                  Lebar Bangunan {property?.building_width} m
                </p>
              </div>
            </div>
            <div className="mb-4 pb-4 border-b border-solid border-[#ECECEC]">
              <p className="text-[24px] font-inter-medium text-[#101010] mb-6">
                Deskripsi
              </p>
              <p
                className={`text-[14px] font-inter text-[#494949] mb-1 whitespace-pre-line ${
                  !showFullDesc ? "text-ellipsis line-clamp-6 " : ""
                }`}
              >
                {description}
              </p>
              {(showFullDesc || description?.length >= 280) && (
                <p
                  onClick={() => setShowFullDesc(true)}
                  className="underline cursor"
                >
                  Selengkapnya
                </p>
              )}
            </div>
            <div className="mb-4 pb-4 border-b border-solid border-[#ECECEC]">
              <p className="text-[24px] font-inter-medium text-[#101010] mb-6">
                Fasilitas Properti
              </p>
              <div className="grid grid-cols-2 gap-x-[80px] gap-y-[16px]">
                {fasilitasProperti?.left?.map((item, index) => (
                  <div
                    key={`fasilitas-kiri-${index}`}
                    className="flex items-center gap-2"
                  >
                    <IDot color="#494949" />
                    <p className="text-[14px] font-inter text-[#494949]">
                      {item}
                    </p>
                  </div>
                ))}
                {fasilitasProperti?.right?.map((item, index) => (
                  <div
                    key={`fasilitas-kanan-${index}`}
                    className="flex items-center gap-2"
                  >
                    <IDot color="#494949" />
                    <p className="text-[14px] font-inter text-[#494949]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            {!!fasilitasSekitar?.length && (
              <div className="mb-4 pb-4 border-b border-solid border-[#ECECEC]">
                <p className="text-[24px] font-inter-medium text-[#101010] mb-6">
                  Fasilitas Sekitar
                </p>
                <div className="grid grid-cols-2 gap-x-[80px] gap-y-[16px]">
                  {fasilitasSekitar?.map((item, index) => (
                    <div
                      key={`fasilitas-kanan-${index}`}
                      className="flex items-center gap-2"
                    >
                      <IDot color="#494949" />
                      <p className="text-[14px] font-inter text-[#494949]">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mb-4 pb-4 border-b border-solid border-[#ECECEC]">
              <p className="text-[24px] font-inter-medium text-[#101010] mb-6">
                Kontak Owner
              </p>
              <p>
                {user?.full_name}, {user?.phone}
              </p>
            </div>
          </div>
        )}

        <div
          className={
            "flex items-center justify-end gap-4 px-4 py-2 border-t border-solid border-[#D8D8D9]"
          }
        >
          <Button
            size="s"
            iconPosition="right"
            icon={IPlane}
            text="Kirim"
            onClick={onSendListing}
          />
        </div>
      </div>

      {isOpenModalImages && (
        <ModalImages
          detailImages={Images}
          setIsOpenModalImages={setIsOpenModalImages}
        />
      )}
    </Modal>
  );
};

const Wrapper = ({ isOpen, ...props }) => {
  if (!isOpen) return null;
  return <ModalDetailListing isOpen={isOpen} {...props} />;
};

export default Wrapper;
